import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { finalize, Observable } from 'rxjs';
import { ConfirmationService } from 'primeng/api';
import { SiteService } from '@card-software/library';
import { ApiResponse } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';

import { AccountingPeriod, AccountingPeriodStatus } from '../../../models/accounting-period/accounting-period';
import { AccountingPeriodProxyService } from '../../../proxies/accounting-period.proxy.service';
import { SiteCompanyService } from '../../../services/site-company.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';

type PeriodAction = 'open' | 'close' | 'closing-only' | 'reopen';

/**
 * #4712 — Accounting Period management. Resolves the current site's financial company, lists a fiscal
 * year's 12 monthly periods, and lets an authorized user generate the year and change each period's
 * status: Open ⇄ ClosingOnly ⇄ Closed, plus Reopen (SAP-style normal + closing intervals, #4711).
 */
@Component({
  selector: 'app-accounting-periods',
  templateUrl: './accounting-periods.component.html',
})
export class AccountingPeriodsComponent implements OnInit {
  private readonly proxy = inject(AccountingPeriodProxyService);
  private readonly siteCompany = inject(SiteCompanyService);
  private readonly translate = inject(TranslateService);
  private readonly confirmationService = inject(ConfirmationService);

  readonly isLoading = signal(0);
  readonly periods = signal<AccountingPeriod[]>([]);
  private readonly companyId = signal<string | null>(null);

  /** Expose the enum to the template for @switch on the status column. */
  readonly Status = AccountingPeriodStatus;
  readonly hasPeriods = computed(() => this.periods().length > 0);

  /** Fiscal year being managed (defaults to the current calendar year). */
  readonly year = new FormControl<number>(new Date().getFullYear(), {
    nonNullable: true,
    validators: [Validators.required, Validators.min(2000), Validators.max(2100)],
  });

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    if (this.year.invalid) {
      GlobalMessageUtil.warn(
        this.t('AP_Period_InvalidYear_Title', 'Invalid year'),
        this.t('AP_Period_InvalidYear', 'Enter a year between 2000 and 2100.'),
      );
      return;
    }
    const fiscalYear = this.year.value;
    this.startLoading();
    this.siteCompany.getFinancialCompany(SiteService.getSelectedSiteId()).subscribe({
      next: (company) => {
        if (!company?.id) {
          this.stopLoading();
          this.companyId.set(null);
          this.periods.set([]);
          GlobalMessageUtil.warn(
            this.t('AP_Period_NoCompany_Title', 'No company'),
            this.t('AP_Period_NoCompany', 'No financial company is linked to the current site.'),
          );
          return;
        }
        this.companyId.set(company.id);
        this.proxy
          .getByCompanyAndYear(company.id, fiscalYear)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe({
            next: (res) => this.periods.set(res.data ?? []),
            error: () => this.periods.set([]),
          });
      },
      error: () => this.stopLoading(),
    });
  }

  generateYear(): void {
    const companyId = this.companyId();
    if (!companyId || this.year.invalid) return;
    const fiscalYear = this.year.value;
    this.confirmationService.confirm({
      header: this.t('AP_Period_Generate_Title', 'Generate periods'),
      message: this.t('AP_Period_Generate_Confirm', 'Create the 12 monthly periods for {{year}}? Existing periods are kept.').replace(
        '{{year}}',
        String(fiscalYear),
      ),
      icon: 'pi pi-calendar-plus',
      accept: () => {
        this.startLoading();
        this.proxy
          .generateYear({ financialCompanyReference: companyId, fiscalYear })
          .pipe(finalize(() => this.stopLoading()))
          .subscribe({
            next: (res) => {
              if (res.success) {
                this.periods.set(res.data ?? []);
                GlobalMessageUtil.success(
                  this.t('AP_Period_Generated_Title', 'Periods ready'),
                  this.t('AP_Period_Generated', 'The fiscal year has been generated.'),
                );
              }
            },
          });
      },
    });
  }

  setClosingOnly(period: AccountingPeriod): void {
    this.changeStatus(period, 'closing-only');
  }
  setClose(period: AccountingPeriod): void {
    this.changeStatus(period, 'close');
  }
  setReopen(period: AccountingPeriod): void {
    this.changeStatus(period, 'reopen');
  }

  private changeStatus(period: AccountingPeriod, action: PeriodAction): void {
    if (!period.id) return;
    const actionLabel = this.actionLabel(action);
    this.confirmationService.confirm({
      header: actionLabel,
      message: this.t('AP_Period_ChangeStatus_Confirm', '{{action}} period {{name}}?')
        .replace('{{action}}', actionLabel)
        .replace('{{name}}', period.periodName),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.startLoading();
        this.callFor(action, period.id!)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe({
            next: (res) => {
              if (res.success && res.data) {
                this.periods.update((rows) => rows.map((r) => (r.id === res.data!.id ? res.data! : r)));
                GlobalMessageUtil.success(
                  this.t('AP_Period_Updated_Title', 'Period updated'),
                  this.t('AP_Period_Updated', 'The period status has been updated.'),
                );
              }
            },
          });
      },
    });
  }

  private callFor(action: PeriodAction, id: string): Observable<ApiResponse<AccountingPeriod>> {
    switch (action) {
      case 'open':
        return this.proxy.open(id);
      case 'close':
        return this.proxy.close(id);
      case 'closing-only':
        return this.proxy.closingOnly(id);
      case 'reopen':
        return this.proxy.reopen(id);
    }
  }

  private actionLabel(action: PeriodAction): string {
    switch (action) {
      case 'open':
        return this.t('AP_Period_Action_Open', 'Open');
      case 'close':
        return this.t('AP_Period_Action_Close', 'Close');
      case 'closing-only':
        return this.t('AP_Period_Action_ClosingOnly', 'Set closing-only');
      case 'reopen':
        return this.t('AP_Period_Action_Reopen', 'Reopen');
    }
  }

  statusLabel(status: AccountingPeriodStatus): string {
    switch (status) {
      case AccountingPeriodStatus.Open:
        return this.t('AP_Period_Status_Open', 'Open');
      case AccountingPeriodStatus.ClosingOnly:
        return this.t('AP_Period_Status_ClosingOnly', 'Closing only');
      case AccountingPeriodStatus.Closed:
        return this.t('AP_Period_Status_Closed', 'Closed');
      default:
        return String(status);
    }
  }

  statusSeverity(status: AccountingPeriodStatus): 'success' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case AccountingPeriodStatus.Open:
        return 'success';
      case AccountingPeriodStatus.ClosingOnly:
        return 'warn';
      case AccountingPeriodStatus.Closed:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }
  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private t(key: string, fallback: string): string {
    const v = this.translate.instant(key);
    return v === key ? fallback : v;
  }
}
